import { Typography } from '@material-ui/core'
import parse from 'html-react-parser'

import { stripHtmlTags } from '../../../../helpers/stripHTML'
import { getTranslate, lang } from '../../../../localization'
import { RatePlanForSearch } from '../../../../types/searchAvailability'
import useStyles from './styles/index.styles'

export function ToolTipContentForCancelPolicy(
  ratePlan: RatePlanForSearch | undefined
) {
  const classes = useStyles({ lang })
  if (
    ratePlan &&
    ratePlan?.cancellationPolicy.length > 0 &&
    ratePlan?.cancellationPolicy[0].description
  ) {
    return (
      <div className={classes.main}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.toolTipText}>
            {getTranslate('Cancelation Policy')}
          </Typography>
        </div>
        <div
          className={classes.content}
          style={{
            direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
            textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
            whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
            unicodeBidi: 'embed' // handle mixed RTL/LTR properly
          }}>
          {parse(
            stripHtmlTags(ratePlan?.cancellationPolicy[0].description).replace(
              /\n/g,
              '<br/>'
            )
          )}
        </div>
      </div>
    )
  }
  const cancelPolicyText = ratePlan?.cancellationPolicy.map((item, i) => {
    let policyText = ''
    let timeUnit = ''

    switch (item.baseFee) {
      case 'ReservationTime':
        policyText = getTranslate('after reservation time:')
        break
      case 'CheckInTime':
        policyText = getTranslate('before the arrival time:')
        break
      case 'AfterCheckInTime':
        policyText = getTranslate('after check-in time:')
        break
      case 'BeforeCheckOutTime':
        policyText = getTranslate('before checkout time:')
        break
      default:
        policyText = ''
        break
    }

    if (item.deadline % 24 === 0) {
      timeUnit =
        item.deadline / 24 !== 1 ? getTranslate('days') : getTranslate('day')
    } else {
      timeUnit =
        item.deadline !== 1 ? getTranslate('hours') : getTranslate('hour')
    }

    if (item.description) {
      return (
        <Typography variant="h6" className={classes.dotText} key={i}>
          {item.description}
        </Typography>
      )
    } else {
      return lang === 'en' ? (
        <div key={item.deadlineType + i.toString() + item.deadline}>
          <Typography variant="h6" className={classes.dotText}>{`${getTranslate(
            'Less than'
          )} ${Math.floor(
            item.deadline % 24 === 0 ? item.deadline / 24 : item.deadline
          )} ${timeUnit} ${policyText}`}</Typography>
          <Typography variant="h6">
            {item.percentage}
            {getTranslate('% of the')}{' '}
            {item.deadlineType === 'FirstNight'
              ? getTranslate('first night price')
              : getTranslate('total price')}{' '}
            {getTranslate('will be charged.')}
          </Typography>
        </div>
      ) : (
        <div key={item.deadlineType + i.toString() + item.deadline}>
          <Typography variant="h6" className={classes.dotText}>
            <Typography variant="h6">{policyText}</Typography>&nbsp;
            {`${getTranslate('Less than')}${Math.floor(
              item.deadline % 24 === 0 ? item.deadline / 24 : item.deadline
            )} ${' '} ${timeUnit}`}
          </Typography>
          <Typography variant="h6">
            {/* just used in Persian */}
            {getTranslate('value')}
            {item.percentage} % {getTranslate('of the')}&nbsp;
            {item.deadlineType === 'FirstNight'
              ? getTranslate('first night price')
              : getTranslate('total price')}
            &nbsp;
            {getTranslate('will be charged')}
          </Typography>
        </div>
      )
    }
  })
  if (
    (Array.isArray(cancelPolicyText) &&
      cancelPolicyText.some((item) => item)) ||
    (typeof cancelPolicyText === 'string' && cancelPolicyText !== '') ||
    (cancelPolicyText && !Array.isArray(cancelPolicyText))
  ) {
    return (
      <div className={classes.main}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.toolTipText}>
            {getTranslate('Cancelation Policy')}
          </Typography>
        </div>
        <div className={classes.content}>{cancelPolicyText}</div>
      </div>
    )
  } else {
    return null
  }
}

export function ToolTipContentForModifyPolicy(
  ratePlan: RatePlanForSearch | undefined
) {
  const classes = useStyles({ lang })
  if (
    ratePlan &&
    ratePlan?.modificationPolicy.length > 0 &&
    ratePlan?.modificationPolicy[0].description
  ) {
    return (
      <div className={classes.main}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.toolTipText}>
            {getTranslate('Modify Policy')}
          </Typography>
        </div>
        <div
          className={classes.content}
          style={{
            direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
            textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
            whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
            unicodeBidi: 'embed' // handle mixed RTL/LTR properly
          }}>
          {parse(
            stripHtmlTags(ratePlan?.modificationPolicy[0].description).replace(
              /\n/g,
              '<br/>'
            )
          )}
        </div>
      </div>
    )
  }
  const modifyPolicyText = ratePlan?.modificationPolicy.map((item, i) => {
    const somethingToShow = item.deadline <= 4320
    if (somethingToShow) {
      let policyText = ''
      let timeUnit = ''

      switch (item.deadlineType) {
        case 'ReservationTime':
          policyText = getTranslate('after reservation time:')
          break
        case 'CheckInTime':
          policyText = getTranslate('before the arrival time:')
          break
        case 'AfterCheckInTime':
          policyText = getTranslate('after check-in time:')
          break
        case 'BeforeCheckOutTime':
          policyText = getTranslate('before checkout time:')
          break
        default:
          return null
      }

      if (item.deadline % 24 === 0) {
        timeUnit =
          item.deadline / 24 !== 1 ? getTranslate('days') : getTranslate('day')
      } else {
        timeUnit =
          item.deadline !== 1 ? getTranslate('hours') : getTranslate('hour')
      }

      return lang === 'en' ? (
        <div key={item.deadlineType + i.toString() + item.deadline}>
          {`${getTranslate('Less than')} ${Math.floor(
            item.deadline % 24 === 0 ? item.deadline / 24 : item.deadline
          )} ${timeUnit} ${policyText}`}
          <br />
          <span>
            {item.percentage}
            {getTranslate('% of the')}{' '}
            {item.baseFee === 'FirstNight'
              ? getTranslate('first night price')
              : getTranslate('total price')}{' '}
            {getTranslate('will be charged.')}
          </span>
        </div>
      ) : (
        <div key={item.baseFee + i.toString() + item.deadline}>
          <div className={classes.mainDeadLineText}>
            <div>{policyText}</div>&nbsp;
            {`${getTranslate('Less than')} ${Math.floor(
              item.deadline % 24 === 0 ? item.deadline / 24 : item.deadline
            )} ${' '} ${timeUnit}`}
          </div>
          <span className={classes.deadLineTextContainer}>
            {item.baseFee === 'FirstNight'
              ? getTranslate('first night price.')
              : getTranslate('total price.')}{' '}
            {getTranslate('will be charged')} &nbsp;
            <div>{getTranslate('of the')}&nbsp;</div>
            {item.percentage} %
          </span>
        </div>
      )
    } else {
      return ''
    }
  })
  if (
    (Array.isArray(modifyPolicyText) &&
      modifyPolicyText.some((item) => item)) ||
    (typeof modifyPolicyText === 'string' && modifyPolicyText !== '') ||
    (modifyPolicyText && !Array.isArray(modifyPolicyText))
  ) {
    return (
      <div className={classes.main}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.toolTipText}>
            {getTranslate('Modify Policy')}
          </Typography>
        </div>
        <div className={classes.content}>{modifyPolicyText}</div>
      </div>
    )
  } else {
    return null
  }
}
